import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Sparkles, Users, Clock, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import CatalogoSubnav from "../../components/CatalogoSubnav";

const TIPOS = ["social", "infantil", "premium", "catering", "boutique", "corporativo", "otro"];

const FORM_VACIO = {
  nombre: "",
  descripcion: "",
  tipo_experiencia: "social",
  precio: 0,
  precio_nino: 0,
  minimo_personas: 30,
  duracion_horas: 5,
  incluye_texto: "",
  activo: true,
  visible_publico: true,
  imagen_url: "",
};

const ExperienciasCRUD = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [seedLoading, setSeedLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalogo`);
      const exps = (res.data || []).filter(i => i.tipo === "experiencia");
      setItems(exps);
    } catch {
      toast.error("Error al cargar experiencias");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const cargarPlantillas = async () => {
    setSeedLoading(true);
    try {
      const { data } = await axios.post(`${API}/tenant/seed-catalogo-demo`);
      const n = data?.agregados?.experiencias || 0;
      if (n > 0) toast.success(`${n} experiencias agregadas. Puedes editarlas cuando quieras.`);
      else toast.info("Ya tenías las experiencias de ejemplo cargadas.");
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar plantillas");
    } finally { setSeedLoading(false); }
  };

  const openNew = () => { setEditing(null); setForm(FORM_VACIO); setDialog(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nombre: it.nombre || "",
      descripcion: it.descripcion || "",
      tipo_experiencia: it.tipo_experiencia || it.tipo_exp || "social",
      precio: it.precio || 0,
      precio_nino: it.precio_nino || 0,
      minimo_personas: it.minimo_personas || 30,
      duracion_horas: it.duracion_horas || 5,
      incluye_texto: Array.isArray(it.incluye) ? it.incluye.join("\n") : (it.incluye_texto || ""),
      activo: it.activo !== false,
      visible_publico: it.visible_publico !== false,
      imagen_url: it.imagen_url || "",
    });
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es requerido"); return; }
    const incluye = form.incluye_texto.split("\n").map(s => s.trim()).filter(Boolean);
    const payload = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion.trim(),
      tipo: "experiencia",
      tipo_experiencia: form.tipo_experiencia,
      precio: parseFloat(form.precio) || 0,
      precio_nino: parseFloat(form.precio_nino) || 0,
      minimo_personas: parseInt(form.minimo_personas) || 0,
      duracion_horas: parseFloat(form.duracion_horas) || 0,
      incluye,
      activo: !!form.activo,
      visible_publico: !!form.visible_publico,
      imagen_url: form.imagen_url || "",
      categoria: "Experiencias",
    };
    try {
      if (editing) {
        await axios.put(`${API}/catalogo/${editing.item_id}`, payload);
        toast.success("Experiencia actualizada");
      } else {
        await axios.post(`${API}/catalogo`, payload);
        toast.success("Experiencia creada");
      }
      setDialog(false);
      await fetchItems();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const eliminar = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/catalogo/${deleteId}`);
      toast.success("Experiencia desactivada");
      await fetchItems();
    } catch {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const toggleActivo = async (it) => {
    try {
      await axios.put(`${API}/catalogo/${it.item_id}`, { activo: !it.activo });
      await fetchItems();
    } catch { toast.error("Error al actualizar"); }
  };

  return (
    <div className="space-y-6" data-testid="experiencias-page">
      <CatalogoSubnav />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Experiencias</h1>
          <p className="text-stone-600 mt-1 text-sm">Catálogo de paquetes que el cliente verá en el cotizador público.</p>
        </div>
        <Button onClick={openNew} className="btn-primary" data-testid="btn-nueva-experiencia">
          <Plus className="w-4 h-4 mr-2" /> Nueva experiencia
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-xl p-12 text-center" data-testid="empty-experiencias">
          <Sparkles className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-xl mb-2">No tienes experiencias</h3>
          <p className="text-stone-600 mb-4 text-sm">Crea paquetes (Social, Infantil, Premium, Catering, etc.) para que el cliente elija en tu cotizador público.</p>
          <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
            <Button onClick={openNew} className="btn-primary" data-testid="btn-crear-primera-experiencia"><Plus className="w-4 h-4 mr-2" />Crear primera experiencia</Button>
            <Button variant="outline" onClick={cargarPlantillas} disabled={seedLoading} data-testid="btn-cargar-plantillas-experiencias">
              {seedLoading ? "Cargando…" : "Cargar plantillas de ejemplo"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(it => (
            <div key={it.item_id} className={`bg-white border rounded-xl p-4 ${it.activo === false ? "opacity-50" : ""}`} style={{ borderColor: "var(--brand-border)" }} data-testid={`exp-card-${it.item_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <h3 className="font-display text-lg text-stone-900 truncate">{it.nombre}</h3>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500">{it.tipo_experiencia || "—"}</p>
                </div>
                <Switch checked={it.activo !== false} onCheckedChange={() => toggleActivo(it)} data-testid={`exp-toggle-${it.item_id}`} />
              </div>
              {it.descripcion && <p className="text-xs text-stone-600 mb-3 line-clamp-2">{it.descripcion}</p>}
              <div className="flex items-center gap-3 text-xs text-stone-600 mb-3">
                <span className="font-semibold text-emerald-900">${(it.precio || 0).toLocaleString("es-MX")}/adulto</span>
                {it.precio_nino > 0 && <span>· ${it.precio_nino.toLocaleString("es-MX")}/niño</span>}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-stone-500 mb-3">
                {it.minimo_personas > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />Min {it.minimo_personas}</span>}
                {it.duracion_horas > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{it.duracion_horas}h</span>}
              </div>
              {Array.isArray(it.incluye) && it.incluye.length > 0 && (
                <div className="text-[11px] text-stone-600 mb-3 line-clamp-3">
                  Incluye: {it.incluye.slice(0, 3).join(", ")}{it.incluye.length > 3 ? ` +${it.incluye.length - 3}` : ""}
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(it)} className="flex-1 border-stone-300 text-xs" data-testid={`exp-edit-${it.item_id}`}>
                  <Edit className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDeleteId(it.item_id)} className="border-red-300 text-red-600" data-testid={`exp-del-${it.item_id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Editar experiencia" : "Nueva experiencia"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej. Experiencia Social" data-testid="exp-input-nombre" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={2} placeholder="Para reuniones sociales, despedidas y celebraciones..." data-testid="exp-input-desc" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <select value={form.tipo_experiencia} onChange={(e) => setForm(f => ({ ...f, tipo_experiencia: e.target.value }))} className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm" data-testid="exp-select-tipo">
                  {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Imagen (URL)</Label>
                <Input value={form.imagen_url} onChange={(e) => setForm(f => ({ ...f, imagen_url: e.target.value }))} placeholder="https://..." data-testid="exp-input-imagen" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Precio adulto ($MXN)</Label>
                <Input type="number" value={form.precio} onChange={(e) => setForm(f => ({ ...f, precio: e.target.value }))} data-testid="exp-input-precio" />
              </div>
              <div>
                <Label>Precio niño ($MXN)</Label>
                <Input type="number" value={form.precio_nino} onChange={(e) => setForm(f => ({ ...f, precio_nino: e.target.value }))} data-testid="exp-input-precio-nino" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Mínimo de personas</Label>
                <Input type="number" value={form.minimo_personas} onChange={(e) => setForm(f => ({ ...f, minimo_personas: e.target.value }))} data-testid="exp-input-min" />
              </div>
              <div>
                <Label>Duración (horas)</Label>
                <Input type="number" step="0.5" value={form.duracion_horas} onChange={(e) => setForm(f => ({ ...f, duracion_horas: e.target.value }))} data-testid="exp-input-duracion" />
              </div>
            </div>
            <div>
              <Label>Qué incluye (uno por línea)</Label>
              <Textarea value={form.incluye_texto} onChange={(e) => setForm(f => ({ ...f, incluye_texto: e.target.value }))} rows={5} placeholder={"Locación 5h\nMenú a elegir\n3 botanas\nBebidas refill"} data-testid="exp-input-incluye" />
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.activo} onCheckedChange={(v) => setForm(f => ({ ...f, activo: v }))} data-testid="exp-switch-activo" />
                Activa
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.visible_publico} onCheckedChange={(v) => setForm(f => ({ ...f, visible_publico: v }))} data-testid="exp-switch-publico" />
                Visible en cotizador público
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={guardar} className="btn-primary" data-testid="exp-btn-guardar">
              {editing ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar experiencia?</AlertDialogTitle>
            <AlertDialogDescription>Se desactivará pero quedará en el histórico. Puedes reactivarla luego.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700" data-testid="exp-btn-confirm-del">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExperienciasCRUD;
